import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { useViewportClass, type ViewportClass } from "../hooks/useViewportClass";

/** Navigation drawer: hidden, hover/tap overlay over the content, or docked in a shelf. */
export type NavDrawerState = "collapsed" | "preview" | "pinned";

/** localStorage key of the pinned preference (per browser, the same on every page). */
export const NAV_PINNED_STORAGE_KEY = "magicbi.navDrawer.pinned";
/** Below this viewport width the drawer cannot pin; a tap opens the preview. */
export const NAV_PIN_MIN_WIDTH = 1024;
/** Delay before a hover preview closes after the pointer leaves the trigger and the menu. */
export const NAV_PREVIEW_CLOSE_DELAY_MS = 250;

const readPinned = (): boolean => {
  try {
    return window.localStorage.getItem(NAV_PINNED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};
const writePinned = (pinned: boolean) => {
  try {
    window.localStorage.setItem(NAV_PINNED_STORAGE_KEY, pinned ? "1" : "0");
  } catch {
    /* storage unavailable: the pin lasts for this session only */
  }
};
const isNarrowViewport = (): boolean => window.innerWidth < NAV_PIN_MIN_WIDTH;

interface LayoutState {
  sidebarVisible: boolean;
  /** Raw drawer state; the exposed `navDrawerState` also applies the narrow-width rule. */
  rawNavDrawerState: NavDrawerState;
  isChatOpen: boolean;
  isPresentMode: boolean;
  isAnnotationsListOpen: boolean;
  currentTab: string;
}

interface LayoutContextType extends Omit<LayoutState, "rawNavDrawerState"> {
  navDrawerState: NavDrawerState;
  /** Open in any form (preview or pinned). */
  navDrawerOpen: boolean;
  /** False below 1024px, where the drawer only previews. */
  navPinAvailable: boolean;
  /** phone < 768, tablet 768-991, desktop >= 992 (from `useViewportClass`). */
  viewport: ViewportClass;
  toggleSidebar: () => void;
  /** Pin / unpin (wide) or open / close the preview (narrow). */
  toggleNavDrawer: () => void;
  /** Closes the preview. A pinned drawer stays docked. */
  closeNavDrawer: () => void;
  /** Hamburger hover: opens the preview unless the pointer has not yet left it since an unpin. */
  /** Pass the pointer event: only a mouse opens the preview. */
  enterNavTrigger: (event?: { pointerType?: string }) => void;
  leaveNavTrigger: () => void;
  enterNavPanel: () => void;
  leaveNavPanel: () => void;
  toggleChat: () => void;
  toggleAnnotationsList: (isClose?: boolean) => void;
  setTab: (tab: string) => void;
  setPresentMode: (value: boolean) => void;
}

/**
 * Routes that render full-bleed and own their own chrome, so the primary
 * navigation sidebar is hidden. `/story/detail` is matched by full prefix so
 * it does not collide with the `/stories` list route.
 */
const isChromelessPath = (pathname: string): boolean =>
  pathname.startsWith("/knowledge-graph") ||
  pathname.startsWith("/story/detail");

const LayoutContext = createContext<LayoutContextType | null>(null);

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<LayoutState>({
    // Hide main sidebar immediately if the app loads directly on a chromeless route.
    sidebarVisible: !isChromelessPath(window.location.pathname),
    rawNavDrawerState: readPinned() ? "pinned" : "collapsed",
    isChatOpen: false,
    isPresentMode: false,
    isAnnotationsListOpen: false,
    currentTab: "",
  });

  const viewport = useViewportClass();
  const [narrow, setNarrow] = useState<boolean>(isNarrowViewport);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressPreviewRef = useRef(false);
  const restorePinRef = useRef(false);

  const location = useLocation();
  const prevPathRef = useRef<string>("");

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => cancelClose, [cancelClose]);

  useEffect(() => {
    const onResize = () => setNarrow(isNarrowViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Present mode takes the full width: unpin on entering, restore on leaving.
  useEffect(() => {
    if (state.isPresentMode) {
      setState((prev) => {
        if (prev.rawNavDrawerState === "collapsed") return prev;
        restorePinRef.current = prev.rawNavDrawerState === "pinned";
        return { ...prev, rawNavDrawerState: "collapsed" };
      });
    } else if (restorePinRef.current) {
      restorePinRef.current = false;
      setState((prev) =>
        prev.rawNavDrawerState === "collapsed" ? { ...prev, rawNavDrawerState: "pinned" } : prev,
      );
    }
  }, [state.isPresentMode]);

  // Handle route-based state initialization and updates
  useEffect(() => {
    // A navigation always closes the drawer.
    // A navigation closes a preview; a pinned drawer stays.
    cancelClose();
    setState((prev) =>
      prev.rawNavDrawerState === "preview" ? { ...prev, rawNavDrawerState: "collapsed" } : prev,
    );
    const match = location.pathname.match(
      /^\/(explore|build|present)\/([^/]+)\/([^/]+)/,
    );
    if (match) {
      const currentTab = match[1];
      setState((prev) => ({
        ...prev,
        isPresentMode: currentTab === "present",
        currentTab,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        isAnnotationsListOpen: false,
        isChatOpen: false,
      }));
    }

    prevPathRef.current = location.pathname;
  }, [location.pathname, cancelClose]);

  // Hide main sidebar when entering a chromeless route; restore when leaving.
  // useLayoutEffect fires before paint so there is no visible flash.
  useLayoutEffect(() => {
    const wasChromeless = isChromelessPath(prevPathRef.current);
    const isChromeless = isChromelessPath(location.pathname);
    if (isChromeless && !wasChromeless) {
      setState((prev) => ({ ...prev, sidebarVisible: false }));
    } else if (!isChromeless && wasChromeless) {
      setState((prev) => ({ ...prev, sidebarVisible: true }));
    }
  }, [location.pathname]);

  const toggleSidebar = useCallback(() => {
    setState((prev) => ({ ...prev, sidebarVisible: !prev.sidebarVisible }));
  }, []);

  const toggleNavDrawer = useCallback(() => {
    cancelClose();
    setState((prev) => {
      const current = prev.rawNavDrawerState;
      if (isNarrowViewport()) {
        return { ...prev, rawNavDrawerState: current === "preview" ? "collapsed" : "preview" };
      }
      if (current === "pinned") {
        // After an unpin the pointer is still on the trigger: no re-preview until it leaves.
        suppressPreviewRef.current = true;
        writePinned(false);
        return { ...prev, rawNavDrawerState: "collapsed" };
      }
      writePinned(true);
      return { ...prev, rawNavDrawerState: "pinned" };
    });
  }, [cancelClose]);

  const closeNavDrawer = useCallback(() => {
    cancelClose();
    setState((prev) =>
      prev.rawNavDrawerState === "preview" ? { ...prev, rawNavDrawerState: "collapsed" } : prev,
    );
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setState((prev) =>
        prev.rawNavDrawerState === "preview" ? { ...prev, rawNavDrawerState: "collapsed" } : prev,
      );
    }, NAV_PREVIEW_CLOSE_DELAY_MS);
  }, [cancelClose]);

  const enterNavTrigger = useCallback((event?: { pointerType?: string }) => {
    cancelClose();
    // Touch and pen have no hover: a tap on a wide screen pins without flashing the preview.
    if (event?.pointerType && event.pointerType !== "mouse") return;
    if (isNarrowViewport() || suppressPreviewRef.current) return;
    setState((prev) =>
      prev.rawNavDrawerState === "collapsed" ? { ...prev, rawNavDrawerState: "preview" } : prev,
    );
  }, [cancelClose]);

  const leaveNavTrigger = useCallback(() => {
    suppressPreviewRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  const enterNavPanel = cancelClose;
  const leaveNavPanel = scheduleClose;

  const toggleChat = useCallback(() => {
    setState((prev) => ({ ...prev, isChatOpen: !prev.isChatOpen }));
  }, []);

  const toggleAnnotationsList = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isAnnotationsListOpen: !prev.isAnnotationsListOpen,
    }));
  }, []);

  const setTab = useCallback((tab: string) => {
    setState((prev) => ({
      ...prev,
      currentTab: tab,
      isPresentMode: tab.toLowerCase() === "present",
    }));
  }, []);

  const setPresentMode = useCallback((value: boolean) => {
    setState((prev) => ({
      ...prev,
      isPresentMode: value,
      isChatOpen: false,
      isAnnotationsListOpen: false,
    }));
  }, []);

  const value = useMemo(
    () => {
      const { rawNavDrawerState, ...rest } = state;
      // Narrow screens never pin: a stored pin shows as collapsed there.
      const navDrawerState: NavDrawerState =
        narrow && rawNavDrawerState === "pinned" ? "collapsed" : rawNavDrawerState;
      return {
      ...rest,
      navDrawerState,
      navDrawerOpen: navDrawerState !== "collapsed",
      navPinAvailable: !narrow,
      viewport,
      enterNavTrigger,
      leaveNavTrigger,
      enterNavPanel,
      leaveNavPanel,
      toggleSidebar,
      toggleNavDrawer,
      closeNavDrawer,
      toggleChat,
      toggleAnnotationsList,
      setTab,
      setPresentMode,
      };
    },
    [
      state,
      narrow,
      viewport,
      enterNavTrigger,
      leaveNavTrigger,
      enterNavPanel,
      leaveNavPanel,
      toggleSidebar,
      toggleNavDrawer,
      closeNavDrawer,
      toggleChat,
      toggleAnnotationsList,
      setTab,
      setPresentMode,
    ],
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};
